// Option sets for the lean onboarding. Each is a plain string list stored
// verbatim on the customisation (value === label); the backend validates them
// against drsecdim's TextChoices. Language is multi-select and matches Play's
// options; every other step is single-choice. Every step defaults to the first
// option, so the wizard never opens in an unselected state.
export const LANGUAGE_OPTIONS = [
	'Python',
	'JavaScript',
	'TypeScript',
	'Go',
	'Java',
	'C#',
	'PHP',
	'Ruby',
	'C',
	'C++',
	'Rust',
	'iOS',
	'Android',
	'Other',
]

export const EXPERIENCE_OPTIONS = [
	'Beginner',
	'Intermediate',
	'Advanced',
	'Senior',
]

export const ROLE_OPTIONS = [
	'Student',
	'Developer',
	'Security Engineer',
	'Security Professional',
	'AI Engineer',
	'Mobile Developer',
	'Incident Responder',
]

export const OBJECTIVE_OPTIONS = [
	'Learn secure coding',
	'Prepare for interviews',
	'Improve AppSec skills',
	'Practice real-world challenges',
	'Build security confidence',
	'Upskill as a Security Engineer',
	'Learn AI security',
	'Learn mobile security',
	'Prepare for incident response',
]

export const COMMITMENT_OPTIONS = [
	'10 minutes a day',
	'30 minutes a day',
	'1 hour a week',
	'2-3 hours a week',
	'Intensive mode',
]

// Every step has a pre-selected default (the first option of each set), so the
// wizard never presents an empty/unselected control.
export const DEFAULT_CUSTOMISATION: UserCustomisation = {
	language: [LANGUAGE_OPTIONS[0]],
	seniority: EXPERIENCE_OPTIONS[0],
	role: ROLE_OPTIONS[0],
	objective: OBJECTIVE_OPTIONS[0],
	timeAvailable: COMMITMENT_OPTIONS[0],
}

// Fills any missing onboarding answers with their defaults so the form always
// opens with a value selected in every single-choice step, while preserving
// anything the learner already saved. Language is only pre-selected for a new
// customisation; an existing one keeps its saved list, even when empty, so the
// learner isn't given a language they never chose.
export const withDefaults = (
	customisation: UserCustomisation | null,
): UserCustomisation => ({
	language: customisation
		? (customisation.language ?? [])
		: DEFAULT_CUSTOMISATION.language,
	seniority: customisation?.seniority || DEFAULT_CUSTOMISATION.seniority,
	role: customisation?.role || DEFAULT_CUSTOMISATION.role,
	objective: customisation?.objective || DEFAULT_CUSTOMISATION.objective,
	timeAvailable:
		customisation?.timeAvailable || DEFAULT_CUSTOMISATION.timeAvailable,
})

// Language can be deselected down to nothing, so the wizard blocks moving on
// until at least one is picked, even for an existing customisation saved
// without one. The single-choice steps always hold a value.
export const isStepAnswered = (
	stepId: WizardStepId,
	form: UserCustomisation,
): boolean => {
	switch (stepId) {
		case 'language':
			return (form.language?.length ?? 0) > 0
		default:
			return true
	}
}

// Normalises the in-progress form into the saved shape. A no-op today, but
// kept as the single seam between the wizard's form state and the saved
// customisation in case that ever diverges again.
export const finalisePreferences = (
	form: UserCustomisation,
): UserCustomisation => ({ ...form })

export interface PreferenceSummaryItem {
	label: string
	value: string
}

const toItem = (
	label: string,
	value: string | undefined,
): PreferenceSummaryItem | undefined =>
	value && value.trim() !== '' ? { label, value } : undefined

export const summarisePreferences = (
	customisation: UserCustomisation,
): PreferenceSummaryItem[] => {
	const items: (PreferenceSummaryItem | undefined)[] = [
		toItem('Language', customisation.language?.join(', ')),
		toItem('Experience', customisation.seniority),
		toItem('Role', customisation.role),
		toItem('Objective', customisation.objective),
		toItem('Commitment', customisation.timeAvailable),
	]
	return items.filter(
		(item): item is PreferenceSummaryItem => item !== undefined,
	)
}

export type WizardStepId =
	| 'language'
	| 'experience-role'
	| 'objective'
	| 'commitment'

export interface WizardStep {
	id: WizardStepId
	title: string
	fields: (keyof UserCustomisation)[]
}

// Four steps: the second pairs Experience with Role; Objective and Commitment
// each get their own step.
export const WIZARD_STEPS: WizardStep[] = [
	{ id: 'language', title: 'Language', fields: ['language'] },
	{
		id: 'experience-role',
		title: 'Experience & role',
		fields: ['seniority', 'role'],
	},
	{ id: 'objective', title: 'Objective', fields: ['objective'] },
	{ id: 'commitment', title: 'Commitment', fields: ['timeAvailable'] },
]

export const WIZARD_STEP_COUNT = WIZARD_STEPS.length

export const isFirstStep = (index: number): boolean => index <= 0

export const isLastStep = (index: number): boolean =>
	index >= WIZARD_STEP_COUNT - 1

export const nextStep = (index: number): number =>
	Math.min(index + 1, WIZARD_STEP_COUNT - 1)

export const prevStep = (index: number): number => Math.max(index - 1, 0)
